import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtNum, fmtL, apiErr } from "@/lib/format";
import DocumentoPdfModal from "@/components/DocumentoPdfModal";

// Preferencia del usuario: mostrar / ocultar la columna «Fecha de facturación» (se recuerda en este equipo)
const PREF_FECHA_FACT = "agroport.inventario.col_fecha_facturacion";
export function usePrefFechaFacturacion() {
  const [ver, setVer] = useState(() => { try { return localStorage.getItem(PREF_FECHA_FACT) !== "0"; } catch { return true; } });
  const cambiar = (v) => { setVer(v); try { localStorage.setItem(PREF_FECHA_FACT, v ? "1" : "0"); } catch { /* sin almacenamiento */ } };
  return [ver, cambiar];
}

export const ND = "No disponible";
const v = (x) => (x === null || x === undefined || x === "" ? <span className="text-text-muted italic">{ND}</span> : x);

/** Ingresos que componen la existencia de un ítem (catálogo · "VER MÁS"). Solo lectura: usa los registros ya importados. */
export default function ExistenciaDetalle({ producto }) {
  const [data, setData] = useState(null);
  const [agotados, setAgotados] = useState(false);
  const [error, setError] = useState("");
  const [verFechaFact, setVerFechaFact] = usePrefFechaFacturacion();
  const [factura, setFactura] = useState(null);     // factura de compra que originó un ingreso (PDF)
  useEffect(() => {
    setData(null); setError("");
    api.get(`/products/${producto.id}/existencia`, { params: { incluir_agotados: agotados } })
      .then(({ data: d }) => setData(d)).catch((e) => setError(apiErr(e)));
  }, [producto.id, agotados]);

  if (error) return <div className="p-4 text-xs text-red-400">{error}</div>;
  if (!data) return <div className="p-4 text-xs text-text-muted">Cargando ingresos…</div>;
  const p = data.producto || {};
  const u = producto.unidad || p.unidad || "";
  const ingresos = data.ingresos || [];
  return (
    <div className="p-4 bg-app/50 border-t border-border/40" data-testid={`existencia-detalle-${producto.codigo}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs mb-3" data-testid="existencia-resumen">
        <span>Existencia física (Σ saldos): <strong>{fmtNum(p.existencia_fisica)} {u}</strong></span>
        <span>− Reservado en proformas: <strong>{fmtNum(p.reservado_proformas)}</strong></span>
        <span>= Disponible (catálogo): <strong className="text-agro-green">{fmtNum(p.disponible)} {u}</strong></span>
        {p.en_transito > 0 && <span className="text-text-muted">En tránsito (no incluido): {fmtNum(p.en_transito)}</span>}
        <label className="ml-auto flex items-center gap-1.5 cursor-pointer text-text-secondary">
          <input type="checkbox" checked={verFechaFact} onChange={(e) => setVerFechaFact(e.target.checked)} className="accent-agro-green" data-testid="existencia-col-fecha-factura" />
          Fecha de facturación
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer text-text-secondary">
          <input type="checkbox" checked={agotados} onChange={(e) => setAgotados(e.target.checked)} className="accent-agro-green" data-testid="existencia-agotados" />
          Incluir ingresos agotados
        </label>
      </div>
      {Math.abs(p.sin_ingreso_registrado || 0) > 0.0001 && (
        <div className="mb-2 text-[11px] text-agro-yellow" data-testid="existencia-sin-ingreso">
          {p.sin_ingreso_registrado > 0
            ? `${fmtNum(p.sin_ingreso_registrado)} ${u} registrados en el ítem no tienen un ingreso que los respalde (lote y batch no disponibles).`
            : `${fmtNum(-p.sin_ingreso_registrado)} ${u} de salidas no tienen un ingreso asignado.`}
        </div>
      )}
      <div className="overflow-x-auto border border-border/40 rounded">
        <table className="w-full text-[11px]">
          <thead className="bg-sidebar/60 text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-2">N° ingreso</th><th className="p-2" title="Ingreso físico (orden PEPS)">Fecha ingreso</th>
              {verFechaFact && <th className="p-2" data-testid="existencia-th-fecha-factura">Fecha de facturación</th>}
              <th className="p-2">Bodega / ubicación</th>
              <th className="p-2">Batch</th><th className="p-2">Lote fabricación</th>
              <th className="p-2">{producto.es_semilla ? "Fecha test" : "Fabricación / vencimiento"}</th>
              <th className="p-2">Proveedor / factura</th><th className="p-2">Origen</th>
              <th className="p-2 text-right">Ingresado</th><th className="p-2 text-right">Salidas</th>
              <th className="p-2 text-right">Saldo</th>
              {data.ver_costos && <>
                <th className="p-2 text-right" title="Precio de compra según la factura (neto de descuento, sin ISV)">Precio compra</th>
                <th className="p-2 text-right" title="Flete y gastos de importación distribuidos a este ingreso (por unidad)">Gastos asig.</th>
                <th className="p-2 text-right" title="Costo unitario de la capa PEPS = precio de compra + gastos asignados">Costo final</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {ingresos.length === 0 ? (
              <tr><td colSpan="16" className="p-4 text-center text-text-muted">Sin ingresos con saldo para este ítem.</td></tr>
            ) : ingresos.map((e, i) => (
              <tr key={e.entry_id || `sin-${i}`} className={`border-t border-border/30 ${e.tipo_fila === "sin_ingreso" ? "bg-agro-yellow/5" : ""}`}>
                <td className="p-2 font-mono">{e.tipo_fila === "sin_ingreso" ? <span className="text-agro-yellow">Sin ingreso registrado</span> : v(e.numero_ingreso)}</td>
                <td className="p-2">{v(e.fecha_ingreso)}</td>
                {verFechaFact && <td className="p-2" data-testid={`existencia-fecha-factura-${i}`}>{v(e.fecha_factura)}</td>}
                <td className="p-2">{v(e.bodega)}{e.ubicacion && <div className="text-text-muted">{e.ubicacion}</div>}</td>
                <td className="p-2 font-mono">{v(e.batch)}</td>
                <td className="p-2 font-mono">{v(e.lote_fabricacion)}</td>
                <td className="p-2">{producto.es_semilla ? v(e.fecha_test) : <>{v(e.fecha_fabricacion)} / {v(e.fecha_vencimiento)}</>}</td>
                <td className="p-2">{v(e.proveedor)}
                  {e.factura_compra_id ? (
                    <button type="button" onClick={() => setFactura(e)} className="block text-agro-green hover:underline" data-testid={`existencia-factura-${i}`}
                      title="Ver la factura de compra de este ingreso">Fact. {e.factura_proveedor || e.factura_compra_numero} · {e.factura_compra_numero}</button>
                  ) : e.factura_proveedor && <div className="text-text-muted">Fact. {e.factura_proveedor}</div>}
                </td>
                <td className="p-2">{e.tipo_fila === "sin_ingreso" ? e.descripcion : <>{v(e.origen)}{e.referencia && <div className="text-text-muted">{e.referencia}</div>}</>}</td>
                <td className="p-2 text-right">{e.cantidad_ingresada === null ? v(null) : fmtNum(e.cantidad_ingresada)}</td>
                <td className="p-2 text-right">{e.salidas === null ? v(null) : fmtNum(e.salidas)}</td>
                <td className="p-2 text-right font-semibold text-agro-green">{fmtNum(e.saldo)}</td>
                {data.ver_costos && <>
                  <td className="p-2 text-right">{e.precio_compra_unitario === undefined || e.precio_compra_unitario === null ? v(null) : fmtL(e.precio_compra_unitario)}</td>
                  <td className="p-2 text-right text-agro-yellow">{e.gasto_asignado_unitario === undefined || e.gasto_asignado_unitario === null ? v(null) : fmtL(e.gasto_asignado_unitario)}
                    {(e.flete_asignado_unitario > 0 || e.otros_gastos_unitario > 0) && <div className="text-[10px] text-text-muted whitespace-nowrap">flete {fmtL(e.flete_asignado_unitario)} · otros {fmtL(e.otros_gastos_unitario)}</div>}</td>
                  <td className="p-2 text-right font-semibold" data-testid={`existencia-costo-${i}`}>{e.costo_unitario === undefined || e.costo_unitario === null ? v(null) : fmtL(e.costo_unitario)}</td>
                </>}
              </tr>
            ))}
          </tbody>
          {ingresos.length > 0 && (
            <tfoot>
              <tr className="border-t border-border font-semibold">
                <td className="p-2" colSpan={verFechaFact ? 11 : 10}>Σ saldos = existencia física</td>
                <td className="p-2 text-right" data-testid="existencia-suma">{fmtNum(ingresos.reduce((t, e) => t + (e.saldo || 0), 0))}</td>
                {data.ver_costos && <td colSpan="3" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {factura && (
        <DocumentoPdfModal url={`/purchase-invoices/${factura.factura_compra_id}/pdf`} titulo="Factura de compra"
          numero={`${factura.factura_compra_numero} · proveedor ${factura.factura_proveedor || ""}`} archivo={`factura-compra-${factura.factura_compra_numero}.pdf`}
          onClose={() => setFactura(null)} testid="existencia-factura-pdf" />
      )}
    </div>
  );
}
